/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { PRODUCT_TYPE } from 'configs/constants';
import monitoringService from 'infrastructure/serviceHandlers/Monitoring/Monitoring';
import { fetchCurrentPlanDetails } from 'services/api/subscriptionApi';
import { RootState } from 'store/store';
import { TCurrentPlansState, TPlanList } from 'typings';

export const fetchCurrentPlan = createAsyncThunk(
  'skus/fetchCurrentPlan',
  async (payload: { skuId: string; price_type: string }, thunkAPI) => {
    try {
      const state = thunkAPI.getState() as RootState;
      const country = state.space.spaceData?.address?.country || 'US';
      const response = await fetchCurrentPlanDetails({
        skuId: payload.skuId,
        country,
        price_type: payload.price_type
      });
      // console.log('current plan Response', response.data);
      return response.data as TPlanList;
    } catch (error) {
      monitoringService.captureException(error, {
        tags: {
          feature: 'fetchCurrentPlan',
          error_type: 'api_error'
        },
        extra: {
          skuId: payload.skuId
        }
      });
      return thunkAPI.rejectWithValue('failed');
    }
  }
);

const initialCurrentPlanState: TCurrentPlansState = {
  currentPlan: null,
  currentPlanCycle: 1,
  currentPlanLevel: 1,
  birdCurrentPlan: null,
  birdCurrentPlanCycle: 1,
  birdCurrentPlanLevel: 1,
  petCurrentPlan: null,
  petCurrentPlanCycle: 1,
  petCurrentPlanLevel: 1,
  trailCurrentPlan: null,
  trailCurrentPlanCycle: 1,
  trailCurrentPlanLevel: 1,
  babyCurrentPlan: null,
  babyCurrentPlanCycle: 1,
  babyCurrentPlanLevel: 1,
  isLoading: false
};

export const currentPlanSlice = createSlice({
  name: 'currentPlan',
  initialState: initialCurrentPlanState,
  reducers: {
    clearCurrentPlanData: (state) => {
      state.currentPlan = null;
      state.currentPlanCycle = 1;
      state.currentPlanCycle = 1;
      state.birdCurrentPlan = null;
      state.birdCurrentPlanCycle = 1;
      state.birdCurrentPlanLevel = 1;
      state.petCurrentPlan = null;
      state.petCurrentPlanCycle = 1;
      state.petCurrentPlanLevel = 1;
      state.trailCurrentPlan = null;
      state.trailCurrentPlanCycle = 1;
      state.trailCurrentPlanLevel = 1;
      state.babyCurrentPlan = null;
      state.babyCurrentPlanCycle = 1;
      state.babyCurrentPlanLevel = 1;
    },
    updatePlanLevelAndCycle: (state, action) => {
      state.currentPlanCycle = action.payload.currentPlanCycle;
      state.currentPlanLevel = action.payload.currentPlanLevel;
    },
    updateBirdPlanLevelAndCycle: (state, action) => {
      state.birdCurrentPlanCycle = action.payload.currentPlanCycle;
      state.birdCurrentPlanLevel = action.payload.currentPlanLevel;
    },
    updatePetPlanLevelAndCycle: (state, action) => {
      state.petCurrentPlanCycle = action.payload.currentPlanCycle;
      state.petCurrentPlanLevel = action.payload.currentPlanLevel;
    },
    updateTrailPlanLevelAndCycle: (state, action) => {
      state.trailCurrentPlanCycle = action.payload.currentPlanCycle;
      state.trailCurrentPlanLevel = action.payload.currentPlanLevel;
    },
    updateBabyPlanLevelAndCycle: (state, action) => {
      state.babyCurrentPlanCycle = action.payload.currentPlanCycle;
      state.babyCurrentPlanLevel = action.payload.currentPlanLevel;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCurrentPlan.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.product_id === PRODUCT_TYPE.HOME_SECURITY) {
          state.currentPlan = action.payload;
          state.currentPlanLevel = action.payload.level;
          state.currentPlanCycle = action.payload.subscription_cycle;
        }
        if (action.payload.product_id === PRODUCT_TYPE.BIRD) {
          state.birdCurrentPlan = action.payload;
          state.birdCurrentPlanLevel = action.payload.level;
          state.birdCurrentPlanCycle = action.payload.subscription_cycle;
        }
        if (action.payload.product_id === PRODUCT_TYPE.PET) {
          state.petCurrentPlan = action.payload;
          state.petCurrentPlanLevel = action.payload.level;
          state.petCurrentPlanCycle = action.payload.subscription_cycle;
        }
        if (action.payload.product_id === PRODUCT_TYPE.TRAIL) {
          state.trailCurrentPlan = action.payload;
          state.trailCurrentPlanLevel = action.payload.level;
          state.trailCurrentPlanCycle = action.payload.subscription_cycle;
        }
        if (action.payload.product_id === PRODUCT_TYPE.BABY) {
          state.babyCurrentPlan = action.payload;
          state.babyCurrentPlanLevel = action.payload.level;
          state.babyCurrentPlanCycle = action.payload.subscription_cycle;
        }
      })
      .addCase(fetchCurrentPlan.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchCurrentPlan.rejected, (state) => {
        state.isLoading = false;
      });
  }
});

// Action creators are generated for each case reducer function
export const { clearCurrentPlanData } = currentPlanSlice.actions;

export default currentPlanSlice.reducer;
